/**
 * API client for the Ringg backend.
 * Pure fetch-based — works in any JS runtime (browser, Node, React Native).
 * URLs are always injected by the platform entry point via a UrlResolver;
 * core embeds no endpoints.
 */
export function createStaticUrlResolver(urls) {
    return {
        resolve(mode) {
            return urls[mode];
        },
    };
}
export function createRinggApiClient(config) {
    const { backendUrl, livekitUrl } = config.urlResolver.resolve(config.mode);
    const authHeaders = config.authorization
        ? { Authorization: config.authorization }
        : config.xApiKey
            ? { "X-API-KEY": config.xApiKey }
            : {};
    // Only present when the caller supplied one. A browser refuses to let us set
    // `Origin` at all, so web must leave `clientOrigin` unset and let the
    // platform attach the real thing.
    const originHeader = config.clientOrigin ? { Origin: config.clientOrigin } : {};
    const headers = () => ({ "Content-Type": "application/json", ...originHeader, ...authHeaders });
    return {
        backendUrl,
        livekitUrl,
        async startWebcall(params) {
            const response = await fetch(`${backendUrl}/calling/webcall`, {
                method: "POST",
                body: JSON.stringify({
                    agent_id: params.agentId,
                    custom_args_values: params.variables,
                    media_type: params.mediaType,
                    // Marks this call as coming from the v2 embed so the backend can use
                    // the newer endpoint; absent on older embeds, which fall back.
                    embed_version: "v2",
                }),
                headers: headers(),
            });
            // Read as text first, then parse — a non-JSON error page shouldn't crash
            // the widget with a cryptic JSON parse error.
            const responseText = await response.text();
            let data;
            try {
                data = JSON.parse(responseText);
            }
            catch {
                throw new Error(responseText.slice(0, 200) || `Request failed with status ${response.status}`);
            }
            if (!response.ok) {
                throw new Error(data?.message || "Failed to make call");
            }
            return {
                call_id: data.call_id,
                user_token: data.user_token,
                enabled_slash_commands: data.enabled_slash_commands,
            };
        },
        async submitFeedback(callId, feedback) {
            await fetch(`${backendUrl}/calling/${callId}/feedback`, {
                method: "POST",
                body: JSON.stringify(feedback),
                headers: headers(),
            });
        },
        async callComponentApi(method, endpoint, payload) {
            const url = endpoint.startsWith("http") ? endpoint : `${backendUrl}${endpoint}`;
            const response = await fetch(url, {
                method,
                body: method === "GET" ? undefined : JSON.stringify(payload),
                headers: headers(),
            });
            // Unchecked parse, same as production — the flow renderer validates shape.
            return (await response.json());
        },
    };
}
